import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Chip,
  Link as MuiLink,
  FormControlLabel,
  Switch
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import React, { useState } from 'react'
import { type PrioritizationReportModel } from './PrioritizationReport.types'
import { generatePrioritizationPDF } from './PrioritizationReportPDF.generator'

interface PDFViewProps {
  reportData: PrioritizationReportModel[]
}

export function PrioritizationReportPDFView (props: PDFViewProps): JSX.Element {
  const { reportData } = props
  const [isGenerating, setIsGenerating] = useState(false)
  const [groupByFacility, setGroupByFacility] = useState(true)

  // Extract metadata from first row
  const isdName = reportData[0]?.isdName ?? 'Unknown ISD'
  const timespanName = reportData[0]?.timespanName ?? 'Unknown Timespan'

  // Extract first facility from priority locations
  const getFirstFacility = (priorityLocations: string | null): string => {
    if (priorityLocations == null || priorityLocations.length === 0) return 'Unknown Facility'
    const firstLocation = priorityLocations.split(', ')[0]
    // Format is "Provider Name - Facility Name", return the full string as facility identifier
    return firstLocation
  }

  // Group children by their first choice facility (if grouping is enabled)
  const groupedData = groupByFacility
    ? reportData.reduce<Record<string, typeof reportData>>((acc, child) => {
      const firstFacility = getFirstFacility(child.priorityLocations)
      if (acc[firstFacility] == null) {
        acc[firstFacility] = []
      }
      acc[firstFacility].push(child)
      return acc
    }, {})
    : { 'All Children': reportData }

  // Sort facility names alphabetically
  const sortedFacilities = Object.keys(groupedData).sort()

  const handleDownloadPDF = async (): Promise<void> => {
    setIsGenerating(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 100))

      generatePrioritizationPDF(reportData, {
        isdName,
        timespanName,
        groupByFacility
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (dateString == null) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  // Calculate age from DOB
  const calculateAge = (dobString: string): number => {
    const dob = new Date(dobString)
    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const monthDiff = today.getMonth() - dob.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--
    }
    return age
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Switch
              checked={groupByFacility}
              onChange={(e) => { setGroupByFacility(e.target.checked) }}
            />
          }
          label="Group by First Choice Facility"
        />
        <Button
          variant="contained"
          size="medium"
          startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadPDF}
          disabled={isGenerating}
          sx={{ bgcolor: '#367575', '&:hover': { bgcolor: '#2A5C5C' } }}
        >
          {isGenerating ? 'Generating...' : 'Download PDF Report'}
        </Button>
      </Box>

      {/* Page-width container for entire report */}
      <Box
        sx={{
          maxWidth: '8.5in',
          mx: 'auto',
          bgcolor: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          p: { xs: 2, sm: 3 },
          borderRadius: 1
        }}
      >
        {/* Report metadata cards */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 140, flex: '1 1 auto' }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>ISD</Typography>
            <Typography variant="body1" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{isdName}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 140, flex: '1 1 auto' }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>School Year</Typography>
            <Typography variant="body1" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{timespanName}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 140, flex: '1 1 auto' }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Generated</Typography>
            <Typography variant="body1" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{new Date().toLocaleDateString()}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 140, flex: '1 1 auto' }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Total Children</Typography>
            <Typography variant="body1" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{reportData.length}</Typography>
          </Box>
        </Box>

        {/* Data Preview - Grouped by Facility (or all children) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, color: '#333', fontWeight: 'bold' }}>
            {groupByFacility ? 'Report Preview (Grouped by First Choice Facility)' : 'Report Preview'}
          </Typography>

          {sortedFacilities.map((facilityName, facilityIdx) => {
            const facilityChildren = groupedData[facilityName]
            return (
              <Box key={facilityIdx} sx={{ mb: 3 }}>
                {/* Facility Header (only show if grouping is enabled) */}
                {groupByFacility && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                    <Typography
                      variant="subtitle2"
                      component="h4"
                      sx={{
                        bgcolor: '#367575',
                        color: 'white',
                        px: 2,
                        py: 0.75,
                        borderRadius: 1,
                        fontWeight: 'bold',
                        fontSize: '0.9rem'
                      }}
                    >
                      {facilityName}
                    </Typography>
                    <Chip
                      label={`${facilityChildren.length} ${facilityChildren.length === 1 ? 'child' : 'children'}`}
                      size="small"
                      sx={{ bgcolor: '#E3F2FD', fontWeight: 'bold', fontSize: '0.75rem' }}
                    />
                    {facilityChildren[0]?.providerTotalCapacity != null && (
                      <>
                        <Chip
                          label={`Capacity: ${facilityChildren[0].providerTotalCapacity}`}
                          size="small"
                          sx={{ bgcolor: '#F5F5F5', fontWeight: 'bold', fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={`Active: ${facilityChildren[0].providerActiveSeats ?? 0}`}
                          size="small"
                          sx={{ bgcolor: '#E8F5E9', fontWeight: 'bold', fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={`Available: ${(facilityChildren[0].providerTotalCapacity ?? 0) - (facilityChildren[0].providerActiveSeats ?? 0)}`}
                          size="small"
                          sx={{ bgcolor: '#FFF3E0', fontWeight: 'bold', fontSize: '0.7rem' }}
                        />
                      </>
                    )}
                  </Box>
                )}

                {/* Children for this facility */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: groupByFacility ? 1 : 0 }}>
                  {facilityChildren.map((child, idx) => {
                    // Get other priority locations (exclude first one if grouped, show all if not)
                    const locations = groupByFacility
                      ? child.priorityLocations?.split(', ').slice(1).join(', ') ?? ''
                      : child.priorityLocations ?? ''
                    return (
                      <Paper key={idx} sx={{ p: 1.5, boxShadow: 1, borderLeft: '3px solid #367575' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#367575', fontSize: '0.85rem' }}>
                                #{idx + 1}
                              </Typography>
                              <MuiLink
                                href={`/CareNetworkManagement/Details/${child.childID}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`View details for ${child.childName} (opens in new window)`}
                                sx={{
                                  fontWeight: 'bold',
                                  color: '#367575',
                                  fontSize: '0.95rem',
                                  textDecoration: 'none',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  '&:hover': {
                                    textDecoration: 'underline'
                                  }
                                }}
                              >
                                {child.childName}
                                <OpenInNewIcon sx={{ fontSize: '14px' }} />
                              </MuiLink>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                              Age: {calculateAge(child.dob)} | App Date: {formatDate(child.appDate)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#E3F2FD', borderRadius: 1, minWidth: 60 }}>
                              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem', display: 'block' }}>Score</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#367575', fontSize: '0.85rem' }}>
                                {child.priorityScore}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 60 }}>
                              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem', display: 'block' }}>FPL</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333', fontSize: '0.85rem' }}>
                                {child.fpl != null ? `${child.fpl}%` : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Priority factors */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          {child.iep && (
                            <Chip
                              icon={<CheckCircleIcon aria-label="Yes" sx={{ fontSize: '14px', color: '#367575' }} />}
                              label="IEP"
                              size="small"
                              sx={{ fontSize: '11px', height: 22, bgcolor: '#E7F3F2' }}
                            />
                          )}
                          {child.foster && (
                            <Chip
                              icon={<CheckCircleIcon aria-label="Yes" sx={{ fontSize: '14px', color: '#367575' }} />}
                              label="Foster"
                              size="small"
                              sx={{ fontSize: '11px', height: 22, bgcolor: '#E7F3F2' }}
                            />
                          )}
                          {child.homeless && (
                            <Chip
                              icon={<CheckCircleIcon aria-label="Yes" sx={{ fontSize: '14px', color: '#FF9800' }} />}
                              label="Homeless"
                              size="small"
                              sx={{ fontSize: '11px', height: 22, bgcolor: '#FFF3E0' }}
                            />
                          )}
                          {child.snap && (
                            <Chip
                              icon={<CheckCircleIcon aria-label="Yes" sx={{ fontSize: '14px', color: '#367575' }} />}
                              label="SNAP"
                              size="small"
                              sx={{ fontSize: '11px', height: 22, bgcolor: '#E7F3F2' }}
                            />
                          )}
                        </Box>

                        {/* Other priority locations or all locations */}
                        {locations.length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                              {groupByFacility ? 'Other Choices:' : 'Priority Locations:'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                              {locations}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    )
                  })}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
